import { Close } from '@mui/icons-material';
import { AvatarGroup, IconButton } from '@mui/material';
import { groupBy } from 'lodash';
import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';
import { MessagingAttendee } from '../../../../../interfaces/interfaces';
import { setAttendeeMessaging } from '../../../../../reduxToolkit/slices/commonSlice';
import { useSelector, useDispatch } from '../../../../../reduxToolkit/typedRedux';
import { fetchMessagingBoothOpensService } from '../../../../../services/services';
import AttendeeAvatar from '../AttendeeAvatar';
import AttendeesList from './AttendeesList';
import { store } from '../../../../../reduxToolkit/store';

interface MessagingBoothOpensInterface {
  setOpenConversation?: (attendee: MessagingAttendee) => void;
}

const MessagingBoothOpens = ({ setOpenConversation }: MessagingBoothOpensInterface) => {
  const boothChannel = useSelector(({ common }) => common.pusherChannels.boothChannel);
  const user = useSelector(({ common }) => common.user);
  const event = useSelector(({ common }) => common.event);
  const booths = useSelector(({ common }) => common.booths);
  const messaging = useSelector(({ common }) => common.attendeeMessaging);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [opensGrouped, setOpensGrouped] = useState<{ [key: string]: { boothid: string; attendee: MessagingAttendee }[] } | undefined>(undefined);
  const [selectedBooth, setSelectedBooth] = useState<{ boothid: string; attendee: MessagingAttendee }[] | undefined>(undefined);

  useEffect(() => {
    if (!event) return;
    if (user?.role === 'PARTICIPANT_ANONYM') return;

    fetchBoothOpens();
  }, [event]);

  useEffect(() => {
    if (!messaging?.boothOpens) return;
    const _grouped = groupBy(messaging.boothOpens, 'boothid');
    setOpensGrouped(_grouped);
  }, [messaging?.boothOpens]);

  const isOrgMember = (boothid: string) => {
    const _booth = booths.find((b) => b._id === boothid);
    return !_booth || _booth.organization._id !== user?.organization?._id;
  };

  // update the messaging redux state with the new booth open data
  // we use filter to remove any booth open with the same data to then append it on top
  const updateMessagingBoothOpens = (boothid: string, attendee: MessagingAttendee) => {
    const _messaging = store.getState().common.attendeeMessaging;
    const _messagingBoothOpens = _messaging?.boothOpens
      ? [..._messaging?.boothOpens.filter((bo) => bo.attendee._id !== attendee._id || bo.boothid !== boothid)]
      : [];
    const _boothOpens = [{ boothid, attendee, datetime_created: new Date() }, ..._messagingBoothOpens];
    dispatch(setAttendeeMessaging({ ..._messaging, boothOpens: _boothOpens }));
  };

  useEffect(() => {
    if (!boothChannel) return;
    if (user?.role === 'PARTICIPANT_ANONYM') return;
    boothChannel.bind('booth-open', ({ boothid, attendee }: { boothid: string; attendee: MessagingAttendee }) => {
      console.log('vystem Realtime > Booth open event ', { boothid, attendee });
      if (!isOrgMember(boothid)) return;
      updateMessagingBoothOpens(boothid, attendee);
    });
    return () => {
      boothChannel.unbind('booth-open');
    };
  }, [boothChannel]);

  const fetchBoothOpens = async () => {
    if (!event) return;
    const { data } = await fetchMessagingBoothOpensService(event._id);
    dispatch(setAttendeeMessaging({ ...messaging, boothOpens: data }));
  };

  return (
    <div className="flex flex-col w-full h-full">
      <div className="sticky z-10 flex flex-row items-center justify-between w-full h-16 px-4 py-8 shadow-sm">
        <p className="text-xl font-bold">{t('event:messaging.booth-opens')}</p>
        {selectedBooth && (
          <IconButton onClick={() => setSelectedBooth(undefined)} size="large">
            <Close />
          </IconButton>
        )}
      </div>
      <div className="flex flex-col w-full overflow-auto">
        {!selectedBooth &&
          opensGrouped &&
          Object.keys(opensGrouped).map((key) => (
            <div
              className="flex flex-row items-center w-full h-16 px-4 py-2 transition duration-200 ease-in-out bg-white border-b-2 cursor-pointer hover:bg-slate-200"
              onClick={() => {
                setSelectedBooth(opensGrouped[key]);
              }}>
              <div className="flex flex-row items-center justify-start w-1/6 h-full">
                <AvatarGroup max={2}>
                  {opensGrouped[key].map((bo) => (
                    <AttendeeAvatar attendee={bo.attendee} />
                  ))}
                </AvatarGroup>
              </div>
              {opensGrouped[key].length === 1 && (
                <span>
                  <p
                    dangerouslySetInnerHTML={{
                      __html: t('event:messaging.open-booth-notif-1', {
                        username: opensGrouped[key][0].attendee?.name,
                        boothname: booths.find((b) => b._id === key)?.name,
                      }),
                    }}></p>
                </span>
              )}
              {opensGrouped[key].length === 2 && (
                <span>
                  <p
                    dangerouslySetInnerHTML={{
                      __html: t('event:messaging.open-booth-notif-2', {
                        first_username: opensGrouped[key][0].attendee?.name,
                        second_username: opensGrouped[key][1].attendee?.name,
                        boothname: booths.find((b) => b._id === key)?.name,
                      }),
                    }}></p>
                </span>
              )}
              {opensGrouped[key].length > 2 && opensGrouped[key].length < 102 && (
                <span>
                  <p
                    dangerouslySetInnerHTML={{
                      __html: t('event:messaging.open-booth-notif-2-more', {
                        first_username: opensGrouped[key][0].attendee?.name,
                        second_username: opensGrouped[key][1].attendee?.name,
                        boothname: booths.find((b) => b._id === key)?.name,
                        visitors_number: opensGrouped[key].length - 2,
                      }),
                    }}></p>
                </span>
              )}
              {opensGrouped[key].length > 102 && (
                <span>
                  <p
                    dangerouslySetInnerHTML={{
                      __html: t('event:messaging.open-booth-notif-102-more', {
                        first_username: opensGrouped[key][0].attendee?.name,
                        second_username: opensGrouped[key][1].attendee?.name,
                        boothname: booths.find((b) => b._id === key)?.name,
                      }),
                    }}></p>
                </span>
              )}
            </div>
          ))}
        {!!selectedBooth && (
          <AttendeesList
            attendees={selectedBooth.map((b) => b.attendee)}
            attendeesCount={selectedBooth.length}
            isLoading={false}
            showSkeleton={false}
            setOpenConversation={setOpenConversation}
          />
        )}
      </div>
    </div>
  );
};
export default MessagingBoothOpens;
